import { useCallback, useEffect, useRef, useState } from "react";
import { useEscapeEbene } from "../overlay/useEscapeEbene.js";
/**
 * @param anzahl  wie viele Zeilen die Liste hat. Der Hook RECHNET nicht damit — er kennt
 *   nur die Zeilen, die sich über `merke` gemeldet haben, und das sind genau die
 *   gezeichneten. Der Parameter steht trotzdem in der Signatur: Er macht die Absicht am
 *   Aufrufort lesbar („diese Liste hat n Zeilen"), und er ist der Platz, an dem eine
 *   spätere Tastaturbedienung ihre Grenzen findet. Zusätzlich dient er als
 *   Abhängigkeits-Marke: Schrumpft die Liste, während jemand zieht (ein Entfernen in einem
 *   anderen Fenster), räumt der Hook seine Zeilenlagen auf, statt auf Elemente zu zeigen,
 *   die nicht mehr im Baum stehen.
 * @param beiAblage wird beim Loslassen über einer ANDEREN Zeile gerufen
 */
export function useZiehOrdnung(anzahl, beiAblage) {
    const [von, setVon] = useState(null);
    const [ziel, setZiel] = useState(null);
    /** Die Zeilen-Elemente, damit der Hook ihre Lage messen kann. */
    const zeilen = useRef(new Map());
    /** Der jüngste Rückruf — ohne diese Bindung hinge der Fensterhorcher am ersten
     *  (dieselbe Falle wie `onCloseRef` in `useFocusTrap`). */
    const ablageRef = useRef(beiAblage);
    ablageRef.current = beiAblage;
    /** Die Kennung DES Zeigers, der den Griff gegriffen hat (Fixrunde 1, S2-5). */
    const zeigerId = useRef(null);
    /** Den Zug beenden, ohne zu melden — der eine Weg heraus für Escape, `pointercancel` und
     *  das Ende einer Ablage. */
    const beenden = useCallback(() => {
        zeigerId.current = null;
        setVon(null);
        setZiel(null);
    }, []);
    /** Über welcher Zeile steht der Zeiger? `null` = über keiner. */
    const zeileUnter = useCallback((y) => {
        const lagen = [];
        for (const [index, element] of zeilen.current) {
            const kasten = element.getBoundingClientRect();
            if (kasten.height === 0)
                continue;
            if (y >= kasten.top && y <= kasten.bottom)
                return index;
            lagen.push({ index, mitte: kasten.top + kasten.height / 2 });
        }
        // Ausserhalb aller Zeilen: die NÄCHSTE nehmen. Wer über den Rand der Liste
        // hinauszieht, meint den Anfang oder das Ende — und ein Ziehen, das dort nichts tut,
        // fühlt sich kaputt an.
        if (lagen.length === 0)
            return null;
        let naechste = lagen[0];
        for (const lage of lagen) {
            if (Math.abs(lage.mitte - y) < Math.abs(naechste.mitte - y))
                naechste = lage;
        }
        return naechste.index;
    }, []);
    /**
     * Schrumpft die Liste, räumt der Hook die Lagen der verschwundenen Zeilen. React ruft
     * den `ref`-Rückruf mit `null`, aber nur, wenn der Rückruf sich ÄNDERT oder das Element
     * geht — bei einer Liste, deren Schlüssel bleiben, ist das nicht zugesagt. Der Riegel
     * hier ist billig und macht `anzahl` zur wirksamen Angabe.
     */
    useEffect(() => {
        for (const index of [...zeilen.current.keys()]) {
            if (index >= anzahl)
                zeilen.current.delete(index);
        }
    }, [anzahl]);
    useEffect(() => {
        if (von === null)
            return;
        // Der gezogene Index als KONSTANTE: In den Rückrufen unten ist `von` wieder
        // `number | null` — die Verengung durch den Riegel darüber gilt in einer Closure nicht
        // (der Typprüfer kann nicht wissen, wann sie läuft). Die Konstante hält den Wert, mit
        // dem dieser Lauf begonnen hat, und das ist auch fachlich der richtige: Ein Neurendern
        // mitten im Ziehen darf die Herkunft nicht verschieben.
        const gezogen = von;
        /**
         * Gehört dieses Ereignis zu UNSEREM Zeiger? (Fixrunde 1, S2-5)
         *
         * Auf einem Tablett liegt oft ein zweiter Finger auf dem Glas, und am Schreibtisch
         * gibt es Maus und Trackpad gleichzeitig. Ohne diesen Riegel steuerte JEDER Zeiger den
         * laufenden Zug: Die Ablagemarke sprang dorthin, wo der andere Finger lag, und dessen
         * Abheben legte ab — eine Reihenfolge, die niemand gezogen hatte.
         */
        function unser(e) {
            return zeigerId.current === null || e.pointerId === zeigerId.current;
        }
        function bewegt(e) {
            if (!unser(e))
                return;
            setZiel(zeileUnter(e.clientY));
        }
        function los(e) {
            if (!unser(e))
                return;
            const nach = zeileUnter(e.clientY);
            beenden();
            // NICHTS tun, wenn dieselbe Zeile: Ein Klick auf den Griff ist keine Umsortierung,
            // und ein Schreibweg für eine unveränderte Reihenfolge wäre eine Version zu viel.
            if (nach !== null && nach !== gezogen)
                void ablageRef.current(gezogen, nach);
        }
        /**
         * `pointercancel` ist ein ABBRUCH und keine Ablage (Fixrunde 1, S2-4).
         *
         * Der Browser schickt es, wenn der Zeiger der Anwendung entzogen wird: eine
         * Systemgeste, ein eingehender Anruf, der Finger verlässt das Glas am Rand, das
         * Fenster verliert seinen Platz. In all diesen Fällen hat NIEMAND eine Stelle gewählt
         * — vorher lief hier derselbe Behandler wie bei `pointerup` und speicherte die letzte
         * zufällige Lage als neue Reihenfolge.
         */
        function verworfen(e) {
            if (!unser(e))
                return;
            beenden();
        }
        window.addEventListener("pointermove", bewegt);
        window.addEventListener("pointerup", los);
        window.addEventListener("pointercancel", verworfen);
        return () => {
            window.removeEventListener("pointermove", bewegt);
            window.removeEventListener("pointerup", los);
            window.removeEventListener("pointercancel", verworfen);
        };
    }, [von, zeileUnter, beenden]);
    /**
     * ESCAPE bricht den Zug ab — über den EBENENSTAPEL und nicht über einen eigenen Horcher
     * (Fixrunde 1, S1-1).
     *
     * Solange gezogen wird, ist der Zug die oberste Schicht: Er hat als Letzter „etwas
     * aufgemacht", und die Hausregel des Stapels ist „wer zuletzt aufmacht, bekommt es
     * zuerst wieder zu". Das ist hier keine Feinheit, sondern der Unterschied zwischen
     * „Zeile falsch gegriffen" und „Maske weg": Der Kasten „Anhänge" steht im
     * Bearbeitungs-Modal einer Detailansicht, und das Modal horcht selbst auf Escape. Zwei
     * Horcher am selben Ziel sehen DASSELBE Ereignis — `stopPropagation` hilft dort nicht
     * (die Begründung steht ausführlich in `useEscapeEbene.ts`), also brach ein Escape
     * mitten im Zug den Zug ab UND schloss das Modal darunter.
     */
    useEscapeEbene(von !== null, beenden);
    const griff = useCallback((index) => ({
        onPointerDown: (e) => {
            // NUR die Haupttaste (Maus), und kein Rechtsklick-Ziehen. Ein Finger meldet
            // `button === 0` wie die linke Maustaste.
            if (e.button !== 0)
                return;
            // Das native Textmarkieren unterdrücken — sonst zieht der Zeiger eine Auswahl über
            // die halbe Seite mit.
            e.preventDefault();
            zeigerId.current = e.pointerId;
            /*
             * DEN ZEIGER EINFANGEN (Fixrunde 1, S2-5).
             *
             * Ohne das verliert ein Zug seine Ereignisse, sobald der Finger ein Element
             * überstreicht, das sie selbst verarbeitet — ein eingebettetes Fenster, ein Feld mit
             * eigener Zeigerbehandlung, eine überlagerte Schicht. Der Zug bliebe dann hängen:
             * `data-zieht` stünde noch, aber kein `pointerup` käme mehr an.
             *
             * Mit `?.`, weil jsdom die Methode nicht kennt (die Tests dieses Pakets liefen sonst
             * in einen TypeError), und in try/catch, weil ein Browser bei einer Kennung, die er
             * nicht mehr führt, eine NotFoundError wirft. Beides darf den Zug nicht verhindern:
             * Das Einfangen ist eine VERBESSERUNG, keine Voraussetzung — die Horcher hängen am
             * Fenster und bekommen ihre Ereignisse auch ohne es.
             */
            try {
                e.target.setPointerCapture?.(e.pointerId);
            }
            catch { /* kein Einfangen möglich — der Zug läuft trotzdem */ }
            setVon(index);
            setZiel(index);
        },
        role: "button",
        // Der Griff ist NICHT tastaturerreichbar und für Vorlesewerkzeuge unsichtbar: Er ist
        // die ZWEITE Bedienart, und die erste (die Pfeilknöpfe) ist tastaturfähig und
        // beschriftet. Ein Griff, den man anfokussieren kann, aber nicht bedienen, wäre eine
        // Falle — dasselbe `tabIndex={-1}` wie am Griff des Positions-Editors.
        tabIndex: -1,
        "aria-hidden": true,
    }), []);
    /**
     * Die Daten-Attribute einer Zeile. `data-ablage` sagt, auf WELCHER SEITE der Strich
     * erscheint: Zieht man nach unten, gehört er UNTER die Zielzeile, nach oben DARÜBER —
     * sonst zeigt die Marke auf eine Stelle, an der die Zeile hinterher nicht landet.
     */
    const zeile = useCallback((index) => {
        const eigenschaften = {};
        if (von === index)
            eigenschaften["data-zieht"] = "";
        if (von !== null && ziel === index && ziel !== von) {
            eigenschaften["data-ablage"] = ziel > von ? "nach" : "vor";
        }
        return eigenschaften;
    }, [von, ziel]);
    /** Die Zeilen melden sich beim Hook, damit er ihre Lage messen kann. Der Aufrufer hängt
     *  `ref={ordnung.merke(i)}` an die Zeile; ein `null` (die Zeile verschwindet) räumt den
     *  Eintrag, sonst zeigte der Hook nach dem Entfernen einer Anlage auf ein Element, das
     *  nicht mehr im Baum steht. */
    const merke = useCallback((index) => (element) => {
        if (element === null)
            zeilen.current.delete(index);
        else
            zeilen.current.set(index, element);
    }, []);
    return { griff, zeile, merke, zieht: von !== null };
}
//# sourceMappingURL=useZiehOrdnung.js.map